import React, { useState, useEffect } from 'react';
import {
    CheckCircle2, XCircle, AlertCircle, Clock,
    Search, Filter, ExternalLink, MessageSquare,
    MoreVertical, FileText, Activity, ShieldAlert,
    Bot
} from 'lucide-react';
import { apiFetch } from '../../api';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';

const TaskCenterPage = ({ token, currentUser }) => {
    const { formatDate } = useTimezoneFormatter();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('My Inbox');
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedTask, setSelectedTask] = useState(null);
    const [rightPaneTab, setRightPaneTab] = useState('overview'); // overview, ai_copilot

    const [actionLoading, setActionLoading] = useState(false);

    // Fetch Tasks
    const fetchTasks = async () => {
        setLoading(true);
        try {
            // For now, fetch ALL tasks if they want filtering on the frontend
            // or pass status=PENDING as a query param.
            const response = await apiFetch('/tasks', token);
            setTasks(response);
            if (response.length > 0 && !selectedTask) {
                // Auto-select first pending task
                const firstPending = response.find(t => t.status === 'PENDING') || response[0];
                handleSelectTask(firstPending.id);
            }
        } catch (error) {
            console.error("Failed to fetch tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchTasks();
    }, [token]);

    const handleSelectTask = async (taskId) => {
        try {
            const taskDetails = await apiFetch(`/tasks/${taskId}`, token);
            setSelectedTask(taskDetails);
        } catch (error) {
            console.error("Failed to fetch task details:", error);
        }
    };

    const handleAction = async (actionStr) => {
        if (!selectedTask) return;
        setActionLoading(true);
        try {
            await apiFetch(`/tasks/${selectedTask.id}/action`, token, {
                method: 'POST',
                body: JSON.stringify({ action: actionStr, comment: "" })
            });
            // Refresh list
            await fetchTasks();
            setSelectedTask(null); // Clear selection or select next
        } catch (e) {
            console.error(`Failed to ${actionStr}:`, e);
        } finally {
            setActionLoading(false);
        }
    };

    // Derived counts
    const pendingCount = tasks.filter(t => t.status === 'PENDING').length;
    const urgentCount = tasks.filter(t => t.status === 'PENDING' && (t.title.toLowerCase().includes('urgent') || t.timeout_hours > 0)).length;

    // Filtering logic
    const filteredTasks = tasks.filter(t => {
        if (activeTab === 'My Inbox') return t.status === 'PENDING';
        if (activeTab === 'Urgent') return t.status === 'PENDING' && (t.title.toLowerCase().includes('urgent') || t.timeout_hours > 0);
        if (activeTab === 'Completed') return ['APPROVED', 'REJECTED', 'ESCALATED'].includes(t.status);
        if (activeTab === 'All Tasks') return true;
        return true;
    }).filter(t => t.title.toLowerCase().includes(searchQuery.toLowerCase()) || (t.description || '').toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <div className="flex h-full bg-gray-50 overflow-hidden font-sans">

            {/* 1. LEFT NAVIGATION PANE */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col items-stretch shrink-0 overflow-y-auto">
                <div className="p-6 pb-2">
                    <h1 className="text-xl font-bold text-gray-900 mb-1">Task Center</h1>
                    <p className="text-xs text-gray-500">Manage approvals & human tasks</p>
                </div>

                <div className="px-4 py-4 space-y-1">
                    {[
                        { name: 'My Inbox', icon: <Clock className="w-4 h-4" />, count: pendingCount, activeColor: 'bg-blue-50 text-blue-700' },
                        { name: 'Urgent', icon: <ShieldAlert className="w-4 h-4" />, count: urgentCount, activeColor: 'bg-red-50 text-red-700' },
                        { name: 'Completed', icon: <CheckCircle2 className="w-4 h-4" />, count: null, activeColor: 'bg-green-50 text-green-700' },
                        { name: 'All Tasks', icon: <Activity className="w-4 h-4" />, count: tasks.length, activeColor: 'bg-gray-100 text-gray-900' },
                    ].map(tab => (
                        <button
                            key={tab.name}
                            onClick={() => setActiveTab(tab.name)}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors
                                ${activeTab === tab.name
                                    ? tab.activeColor
                                    : 'text-gray-600 hover:bg-gray-50'}`}
                        >
                            <div className="flex items-center gap-3">
                                {tab.icon}
                                {tab.name}
                            </div>
                            {tab.count !== null && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.name ? 'bg-white bg-opacity-60' : 'bg-gray-100'
                                    }`}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* 2. TASK LIST PANE */}
            <div className="w-80 bg-white border-r border-gray-200 flex flex-col shrink-0">
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 flex justify-center"><LoadingSpinner /></div>
                    ) : filteredTasks.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 text-sm">No tasks found.</div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {filteredTasks.map(task => (
                                <div
                                    key={task.id}
                                    onClick={() => handleSelectTask(task.id)}
                                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors relative border-l-4 ${selectedTask?.id === task.id ? 'bg-blue-50/50 border-blue-500' : 'border-transparent'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <div className="flex flex-col truncate pr-4">
                                            <h3 className="text-sm font-semibold text-gray-900 truncate">{task.title}</h3>
                                            {task.timeout_hours > 0 && <span className="text-[10px] text-red-500 font-medium flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {task.timeout_hours}h Timeout</span>}
                                        </div>
                                        <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider
                                            ${task.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                                task.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                                    task.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                                        'bg-gray-100 text-gray-800'}`}
                                        >
                                            {task.status}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">{task.description}</p>
                                    <div className="flex items-center text-xs text-gray-400">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {formatDate(task.created_at)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 3 & 4. CONTEXT / CONTENT PANE & ACTION BAR */}
            <div className="flex-1 flex flex-col bg-white overflow-hidden relative">
                {selectedTask ? (
                    <>
                        {/* 4. Action Bar */}
                        <div className="h-16 px-6 border-b border-gray-200 flex items-center justify-between shrink-0 bg-white">
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg font-bold text-gray-900">{selectedTask.title}</h2>
                                {selectedTask.timeout_hours > 0 && (
                                    <span className="text-xs bg-red-100 text-red-700 font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-sm border border-red-200">
                                        <Clock className="w-3 h-3" /> {selectedTask.timeout_hours}h Timeout
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                {selectedTask.status === 'PENDING' && (
                                    <>
                                        <button
                                            onClick={() => handleAction('rejected')}
                                            disabled={actionLoading}
                                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                                        >
                                            Reject
                                        </button>
                                        <button
                                            onClick={() => handleAction('escalated')}
                                            disabled={actionLoading}
                                            className="px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg text-sm font-medium hover:bg-orange-100 transition-colors"
                                        >
                                            Escalate
                                        </button>
                                        <button
                                            onClick={() => handleAction('approved')}
                                            disabled={actionLoading}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                                        >
                                            {actionLoading ? "Processing..." : "Approve"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Middle Nav Tabs */}
                        <div className="px-6 border-b border-gray-200 bg-gray-50/50">
                            <div className="flex space-x-6">
                                <button
                                    onClick={() => setRightPaneTab('overview')}
                                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${rightPaneTab === 'overview' ? 'border-primary text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Overview
                                </button>
                                <button
                                    onClick={() => setRightPaneTab('ai_copilot')}
                                    className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${rightPaneTab === 'ai_copilot' ? 'border-purple-500 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    <Bot className="w-4 h-4" />
                                    AI Copilot
                                </button>
                            </div>
                        </div>

                        {/* Details Content */}
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
                            {rightPaneTab === 'overview' ? (
                                <div className="max-w-4xl mx-auto space-y-6">
                                    {/* Description Card */}
                                    {selectedTask.description && (
                                        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                            <h3 className="text-sm font-bold text-gray-900 mb-2">Instructions</h3>
                                            <p className="text-sm text-gray-600">{selectedTask.description}</p>
                                        </div>
                                    )}

                                    {/* Context Data Grid */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                                            <h3 className="text-sm font-bold text-gray-900">Context Data</h3>
                                            <span className="text-xs text-gray-500 font-mono">Flow ID: {selectedTask.flow_id?.split('-')[0]}</span>
                                        </div>
                                        <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-6">
                                            {Object.entries(selectedTask.context_data || {}).map(([key, value]) => {
                                                // Ignore internal DuckDB route variables if any leak
                                                if (key.startsWith('_')) return null;
                                                return (
                                                    <div key={key}>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                                            {key}
                                                        </label>
                                                        <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded border border-gray-100 break-words whitespace-pre-wrap">
                                                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {Object.keys(selectedTask.context_data || {}).length === 0 && (
                                                <div className="col-span-2 text-sm text-gray-500 text-center py-4">
                                                    No context data provided by the workflow.
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {selectedTask.status !== 'PENDING' && (
                                        <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                                            <p className="text-sm text-gray-600">
                                                This task was marked as <strong>{selectedTask.status}</strong> on {formatDate(selectedTask.resolved_at)}. No further actions are required.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="max-w-4xl mx-auto h-full flex flex-col">
                                    <div className="flex-1 bg-white rounded-xl border border-purple-100 shadow-sm overflow-hidden flex flex-col justify-center items-center text-center p-8">
                                        <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mb-4 border border-purple-100">
                                            <Bot className="w-8 h-8 text-purple-600" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">AI Copilot Analysis</h3>
                                        <p className="text-gray-500 text-sm max-w-md">
                                            This feature connects your custom AI agents to analyze the context data and provide a recommendation. It will be available in a future update.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 px-8 text-center bg-gray-50/50">
                        <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 border border-gray-100">
                            <CheckCircle2 className="w-8 h-8 text-gray-300" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">You're all caught up!</h3>
                        <p className="text-sm text-gray-500 max-w-xs">Select a task from the list to view its details, context data, and take action.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskCenterPage;
